import { useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, EmptyState, Field, Icon, Input, useDesignSystemTheme } from "@xinghai/ui";
import { ApiError, authApi } from "../../lib/api";
import { friendlyMessage } from "../../lib/apiErrors";
import { COPYRIGHT_LINE } from "../../lib/constants";
import { qk } from "../../lib/queries";
import { homeFor, primaryRole } from "../../app/roles";
import { useTheme } from "../../app/theme";
import { confirmIssue, passwordHint, passwordIssues } from "./passwordRules";

/**
 * ACCOUNTS.md §1 — first-admin bootstrap. The server prints a one-time setup
 * token at startup; once an admin exists the page is closed for good.
 */
export function SetupPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { theme } = useTheme();
  useDesignSystemTheme(theme);

  const state = useQuery({
    queryKey: qk.setupState,
    queryFn: async () => {
      try {
        const s = (await authApi.setupState()) as { open?: boolean; required?: boolean };
        // server contract is `{ required: true }` (plus `open` since v2.0.1)
        return { open: s.open === true || s.required === true };
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 404 || err.code === "SETUP_CLOSED" || err.code === "SETUP_DONE")
        ) {
          return { open: false };
        }
        throw err;
      }
    },
    retry: false,
  });

  const [token, setToken] = useState("");
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);

  const setup = useMutation({
    mutationFn: () =>
      authApi.setup({
        token: token.trim(),
        username: username.trim(),
        password,
        display_name: displayName.trim(),
      }),
    onSuccess: (me) => {
      qc.setQueryData(qk.me, me);
      void navigate(homeFor(primaryRole(me.user.roles)), { replace: true });
    },
    onError: (err) => {
      setError(
        friendlyMessage(err, "创建失败，请重试。", {
          INVALID_SETUP_TOKEN:
            "这个 setup token 不对或者已经过期。刷新本页会生成新的 token（写在服务器的 setup-token.txt 里）。",
          SETUP_TOKEN_INVALID:
            "这个 setup token 不对或者已经过期。刷新本页会生成新的 token（写在服务器的 setup-token.txt 里）。",
          SETUP_CLOSED: "系统已经初始化过了，这个页面不再可用。",
          SETUP_DONE: "系统已经初始化过了，这个页面不再可用。",
          USERNAME_TAKEN: "这个用户名已经有人用了，换一个。",
        }),
      );
    },
  });

  if (state.isPending) return <p className="xz-container gz-muted">正在检查…</p>;

  if (!state.data?.open) {
    return (
      <div className="xz-container">
        <EmptyState
          icon={<Icon id="ui-lock" size={24} />}
          title="这个页面不存在"
          description="系统已经完成初始化。需要新账号请让管理员发邀请链接给你。"
        />
      </div>
    );
  }

  const pwIssues = password ? passwordIssues(password, username) : [];
  const mismatch = confirm ? confirmIssue(password, confirm) : null;
  const hint = passwordHint(password, username);

  const submit = () => {
    setError(null);
    if (token.trim().length === 0) return setError("请填写启动时打印的 setup token。");
    if (username.trim().length < 3) return setError("用户名至少 3 位。");
    if (displayName.trim().length === 0) return setError("请填写显示名。");
    if (pwIssues.length > 0 || mismatch || password.length === 0)
      return setError("请先按提示修好密码。");
    setup.mutate();
  };

  if (setup.isSuccess) return <Navigate to="/" replace />;

  return (
    <main className="xz-login">
      <form
        className="xz-login__card gz-panel gz-panel--raised"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <h1 className="gz-type-heading">创建第一个管理员</h1>
        <p className="gz-muted">
          服务第一次启动时会在日志里打印一个 setup token（也写在数据目录的 setup-token.txt）。
          把它填在这里，给自己设一个密码。这个页面只能用一次。
        </p>

        <Field label="Setup token">
          <Input value={token} autoComplete="off" onChange={(e) => setToken(e.target.value)} />
        </Field>
        <Field label="管理员用户名">
          <Input
            value={username}
            autoCapitalize="off"
            autoComplete="username"
            onChange={(e) => setUsername(e.target.value.toLowerCase())}
          />
        </Field>
        <Field label="显示名">
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </Field>
        <Field
          label="密码"
          hint={password ? `${hint.label}：${hint.advice}` : "至少 10 位，不要包含用户名"}
          error={pwIssues[0]}
        >
          <Input
            type="password"
            value={password}
            autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field label="再输一次密码" error={mismatch ?? undefined}>
          <Input
            type="password"
            value={confirm}
            autoComplete="new-password"
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>

        {error && (
          <p className="gz-notice gz-notice--error" role="alert">
            <Icon id="ui-error" size={20} />
            {error}
          </p>
        )}

        <Button type="submit" variant="primary" size="lg" block loading={setup.isPending}>
          创建管理员
        </Button>
        <p className="gz-caption xz-login__org">{COPYRIGHT_LINE}</p>
      </form>
    </main>
  );
}
